import { ExpandMoreTwoTone } from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Divider,
  Hidden,
  List,
  Popover,
  Typography,
  styled,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import UserAvatar from 'switch-fe-shared/components/Avatars/UserAvatar';
import { useFeatureFlag } from 'switch-fe-shared/hooks/useFeatureFlag';
import { PRODUCTS } from 'switch-shared/types/general.enums';

import { useAppSelector } from '../../hooks/hooks';
import { useSearchReleaseNotesQuery } from '../../services/switch-rest-api/release-note.service';
import AccountMenuHeader from './AccountMenuHeader';
import PoweredByFooter from './footer/PoweredByFooter';
import VersionFooter from './footer/VersionFooter';
import BrandSettingsMenuItem from './items/BrandSettingsMenuItem';
import HardwareMenuItem from './items/HardwareMenuItem';
import LogoutMenuItem from './items/LogoutMenuItem';
import ReleaseNotesMenuItem from './items/ReleaseNotesMenuItem';

const RECENT_RELEASE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const EmailLabel = styled(Typography)(
  ({ theme }) => `
    font-weight: ${theme.typography.fontWeightBold};
    color: ${theme.palette.primary.contrastText};
    display: block;
    text-align: center;
    padding-left: ${theme.spacing(1)};
  `
);

const AccountMenu = () => {
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const isSwitchBrandingRemoved = useFeatureFlag(
    'ac_remove_switch_branding__ops'
  );

  const { data: recentReleaseNotes } = useSearchReleaseNotesQuery({
    target: PRODUCTS.ADMIN_CONSOLE,
    limit: '1',
  });
  const mostRecentNote = recentReleaseNotes?.data[0];
  const hasRecentRelease = mostRecentNote
    ? Date.now() - new Date(mostRecentNote.createdAt).getTime() <=
      RECENT_RELEASE_WINDOW_MS
    : false;

  const open = Boolean(anchorEl);
  const close = () => setAnchorEl(null);

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Button
        color='primary'
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant='text'
        sx={{ '&:hover': { backgroundColor: 'transparent' } }}
      >
        <Badge
          color='error'
          variant='dot'
          overlap='circular'
          invisible={!hasRecentRelease}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <UserAvatar user={user?.user} />
        </Badge>
        <Hidden mdDown>
          <EmailLabel variant='body1'>{user?.user?.email ?? ''}</EmailLabel>
        </Hidden>
        <Hidden smDown>
          <ExpandMoreTwoTone
            sx={{ ml: 1, color: theme.colors.neutral.grey[40] }}
          />
        </Hidden>
      </Button>

      <Popover
        anchorEl={anchorEl}
        onClose={close}
        disableScrollLock
        open={open}
        sx={{ marginTop: 1 }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ minWidth: 240 }}>
          <AccountMenuHeader
            displayName={user?.user?.displayName}
            email={user?.user?.email}
            photoURL={user?.user?.photoURL}
          />
          <Divider />
          <List
            component='nav'
            sx={{
              p: 0,
              '& .MuiListItemIcon-root': {
                minWidth: 32,
                color: theme.palette.grey[500],
              },
            }}
          >
            <BrandSettingsMenuItem onActivate={close} />
            <ReleaseNotesMenuItem
              onActivate={close}
              hasNew={hasRecentRelease}
            />
            <HardwareMenuItem onActivate={close} />
            <LogoutMenuItem />
          </List>

          {!isSwitchBrandingRemoved && <PoweredByFooter />}
          <VersionFooter />
        </Box>
      </Popover>
    </Box>
  );
};

export default AccountMenu;
